import styled from '@emotion/styled';
import { NotificationListingWrap } from '@shared/components/core/alertNotification/CommonAlertNotificationStyles';
import InternalNotificationCard from '@shared/components/core/alertNotification/InternalNotificationCard';
import Button from '@shared/components/core/button/CoreButton';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import { CardSubtitle } from '@shared/components/core/text/CardTypography';
import { ContentQuickWrap, ContentTextButton } from '@shared/components/core/text/ContentTypography';
import { BodySmall, BodyXSmall, TitleXSmall } from '@shared/components/core/text/Typography';
import { color } from '@shared/constants/Styles';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useCurrentUserStore } from '@shared/hooks/state/CurrentUserContainer';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import useScrollToElement from '@shared/hooks/useScrollToElement';
import UserPreferencesModel from '@shared/models/users/UserPreferencesModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import DetailsDisclosure from '../core/disclosure/DetailsDisclosure';

export const ProfileConflictIndicatorsId = 'profile_conflict_indicator_id';

const ProfileConflictIndicators: React.FC<{ errorOnly?: boolean }> = ({ errorOnly = false }) => {
    const profileConflictErrors = useEditProfileStore((state) => state.profileConflictErrors);
    const isProfileConflictsAcknowledged = useEditProfileStore((state) => state.isProfileConflictsAcknowledged);
    const profileConflictWarnings = useEditProfileStore((state) => state.profileConflictWarnings);
    const currentUser = useCurrentUserStore((state) => state.currentUser);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [scrollElementID, setScrollElementID] = useState<string | null>(null);
    const triggerScroll = useScrollToElement(scrollElementID);

    // error and warning conditional checks
    const showErrors = profileConflictErrors?.length > 0;
    const showWarningsBecauseNoErrors = !showErrors && profileConflictWarnings?.length > 0;

    const [userWarningOptOut, setUserWarningOptOut] = useState(
        currentUser?.userPreferences?.isWarningConflictIndicatorsOptOut ?? false
    );

    const showWarnings = !userWarningOptOut && !isProfileConflictsAcknowledged && showWarningsBecauseNoErrors;

    // handlers
    const handleConflictItemClick = (scrollID: string, pageLink?: string) => {
        const element = document.getElementById(scrollID);

        if (!element && pageLink) {
            navigate(pageLink);
        } else {
            setScrollElementID(scrollID);
            triggerScroll();
        }
    };

    const handleWarningOptOut = async () => {
        const currentUser = useCurrentUserStore.getState().currentUser;
        const preferences: UserPreferencesModel = {
            ...currentUser?.userPreferences,
            isWarningConflictIndicatorsOptOut: !userWarningOptOut,
            featureFlags: currentUser?.userPreferences?.featureFlags ?? [],
        };

        const result = await useCurrentUserStore.getState().updateUserPreferences(preferences);

        if (result?.resultType == 'Ok') {
            setUserWarningOptOut(!userWarningOptOut);
            useAlertsStore.getState().showSuccess(t('common:account.success.userPreferences'));
        } else {
            useAlertsStore
                .getState()
                .showError(result?.errors?.join(', ') ?? t('common:account.error.userPreferences'));
        }
    };

    if (!showErrors && !showWarnings) return null;

    return (
        <NotificationListingWrap id={ProfileConflictIndicatorsId} as="aside" tabIndex={-1}>
            {showErrors && (
                <InternalNotificationCard
                    title={t('common:profile.conflictIndicators.errorTitle')}
                    icon="warning"
                    color="error"
                    subtitle={t('common:profile.conflictIndicators.subtitle')}>
                    {profileConflictErrors?.map((conflictModel, index) => (
                        <ConflictActionCard key={`${conflictModel.title}${index}_ConflictError`}>
                            <TitleXSmall>{conflictModel.title}</TitleXSmall>
                            {conflictModel.subtitle && <BodyXSmall>{conflictModel.subtitle}</BodyXSmall>}
                            <ConflictItemsWrap>
                                {conflictModel.items.map((item, idx) => (
                                    <StyledConflictItem
                                        key={`${item.key}${idx}_ConflictErrorItem`}
                                        startIcon="warning"
                                        onClick={() => handleConflictItemClick(item.scrollID, item.pageLink)}>
                                        {item.displayString}
                                    </StyledConflictItem>
                                ))}
                            </ConflictItemsWrap>
                        </ConflictActionCard>
                    ))}
                </InternalNotificationCard>
            )}

            {showWarnings && !errorOnly && (
                <InternalNotificationCard
                    title={t('common:profile.conflictIndicators.warningTitle')}
                    icon="warningTriangle"
                    color="warning"
                    subtitle={t('common:profile.conflictIndicators.subtitle')}>
                    {profileConflictWarnings?.map((conflictModel, index) => (
                        <ConflictActionCard key={`${conflictModel.title}${index}_ConflictWarning`}>
                            <CardSubtitle>{conflictModel.title}</CardSubtitle>
                            {conflictModel.subtitle && <BodySmall>{conflictModel.subtitle}</BodySmall>}
                            <ConflictItemsWrap>
                                <DetailsDisclosure title={t('common:profile.conflictIndicators.warningDetails')}>
                                    {conflictModel.items.map((item, idx) => (
                                        <StyledConflictItem
                                            key={`${item.key}${idx}_ConflictWarningItem`}
                                            startIcon="warningTriangle"
                                            onClick={() => handleConflictItemClick(item.scrollID, item.pageLink)}>
                                            {item.displayString}
                                        </StyledConflictItem>
                                    ))}
                                </DetailsDisclosure>
                            </ConflictItemsWrap>
                        </ConflictActionCard>
                    ))}
                    <WarningButtonsWrap>
                        <WarningButtons
                            startIcon="cancel2"
                            $color="secondary"
                            $size="small"
                            onClick={() => useEditProfileStore.getState().setIsConflictsAcknowledged(true)}>
                            {t('common:profile.conflictIndicators.warningIgnore')}
                        </WarningButtons>
                        <WarningButtons
                            startIcon="settings"
                            $color="secondary"
                            $size="small"
                            onClick={handleWarningOptOut}>
                            {t('common:profile.conflictIndicators.warningDoNotShowAgain')}
                        </WarningButtons>
                    </WarningButtonsWrap>
                </InternalNotificationCard>
            )}
        </NotificationListingWrap>
    );
};

const WarningButtons = styled(Button)`
    &:hover {
        color: ${color.bg_light} !important;
    }
`;

const WarningButtonsWrap = styled(ChipButtonContainer)`
    gap: 1rem;
`;

const ConflictItemsWrap = styled(ContentQuickWrap)`
    flex-direction: column;
    display: flex;
    flex-wrap: wrap;
    margin: 1rem 0;
`;

const StyledConflictItem = styled(ContentTextButton)`
    text-align: left;
    justify-content: left;
    text-decoration: underline;
    z-index: 2;
    width: fit-content;
    :hover {
        text-decoration: none;
    }
`;

const ConflictActionCard = styled.div`
    margin: 1rem 0rem;
`;

export default ProfileConflictIndicators;
